import { useQueries, useQuery } from '@tanstack/react-query';
import { organizationsApi } from '../services/api';
import { TAX_RATE } from '../config/pricing';

/**
 * The one definition of the tax-rate query, shared by the single-organization hook
 * and the cart-level one so their cache keys and percent->fraction conversion cannot
 * drift apart.
 */
const taxRateQuery = (organizationId: string) => ({
  queryKey: ['taxRate', organizationId],
  queryFn: () => organizationsApi.getTaxRate(organizationId).then((res) => res.taxRate / 100),
});

/**
 * Single-organization tax rate.
 *
 * NOTE: cart and checkout use `useCartTax` below, which handles the cross-organization
 * case this cannot. Keep using this only where exactly one organization is in play.
 */
export const useTaxRate = (organizationId: string | undefined) => {
  return useQuery({
    ...taxRateQuery(organizationId!),
    enabled: !!organizationId,
  });
};

export const useTaxRateValue = (organizationId: string | undefined): number => {
  const { data, isSuccess } = useTaxRate(organizationId);
  return isSuccess && data !== undefined ? data : TAX_RATE;
};

/** The shape `useCartTax` needs from a cart item. `CartItem` satisfies it structurally. */
export interface TaxableItem {
  price: number;
  quantity: number;
  organizationId: string | undefined;
}

export interface CartTax {
  /** Total tax for the cart, computed per item at its OWN organization's rate. */
  amount: number;
  /**
   * The one rate every item in the cart shares, or `null` when the cart spans
   * organizations whose rates differ — in which case no single percentage
   * honestly describes the line, and the caller must render it without one.
   */
  uniformRate: number | null;
}

/**
 * Tax for a whole cart.
 *
 * The storefront catalog is genuinely cross-organization: for an anonymous shopper
 * the product query applies no organization filter, so a cart can legitimately mix
 * orgs on different rates. Resolving a single rate from `items[0]` therefore charged
 * one organization's rate against another's goods and labelled it confidently.
 *
 * This computes tax per item at its own organization's rate. It deliberately does NOT
 * decide the presentation model — one tax line vs. one per organization, and whether a
 * mixed cart should be allowed at all, are the open questions tracked by roadmap R-033.
 */
export const useCartTax = (items: TaxableItem[]): CartTax => {
  const organizationIds = Array.from(
    new Set(items.map((item) => item.organizationId).filter((id): id is string => Boolean(id))),
  ).sort();

  const results = useQueries({ queries: organizationIds.map(taxRateQuery) });

  const rateByOrganization = new Map<string, number>();
  organizationIds.forEach((organizationId, index) => {
    const result = results[index];
    // Same fallback contract as useTaxRateValue: an unresolved rate degrades to the
    // default rather than blocking the cart.
    rateByOrganization.set(
      organizationId,
      result?.isSuccess && result.data !== undefined ? result.data : TAX_RATE,
    );
  });

  const rateFor = (organizationId: string | undefined): number =>
    (organizationId ? rateByOrganization.get(organizationId) : undefined) ?? TAX_RATE;

  const amount = items.reduce(
    (sum, item) => sum + item.price * item.quantity * rateFor(item.organizationId),
    0,
  );

  if (items.length === 0) {
    return { amount: 0, uniformRate: TAX_RATE };
  }

  const distinctRates = new Set(items.map((item) => rateFor(item.organizationId)));

  return { amount, uniformRate: distinctRates.size === 1 ? [...distinctRates][0] : null };
};
